import React, { useEffect } from 'react';

import thunk from 'redux-thunk';
import { compose, createStore, applyMiddleware } from 'redux';
import { persistStore } from 'redux-persist';
import { Provider, useSelector } from 'react-redux';
import { createLogger } from 'redux-logger';
import { QueryClientProvider } from '@tanstack/react-query';

import rootReducer from 'reducers';
import MainRouter from 'router';
import { queryClient } from './data-fetching';
import { PermissionsContextProvider } from 'contexts/permissionsContext';
import { ConfigContextProvider } from 'contexts/configContext';
import useVisualConfig from 'hooks/useVisualConfig';
import useBootstrap from 'hooks/useBootstrap';
import { applyBootstrapConfig } from 'config';
import { backendUrl, applyRuntimeEnv } from 'config/envConfig';
import { i18n } from 'utils/i18n';
import numeral from 'numeral';
import { getNumeralLocale } from 'locales/numeralLocales';
import 'styles/index.scss';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

export const store = createStore(
  rootReducer,
  undefined,
  compose(
    composeEnhancers(applyMiddleware(thunk, createLogger()))
  ),
)

const findCachedItem = (prefix, tid) => {
  if (tid) {
    const exact = localStorage.getItem(`${prefix}${tid}`);
    if (exact) return exact;
  }
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const val = localStorage.getItem(key);
      if (val) return val;
    }
  }
  return null;
};

const ServiceUnavailable = ({ brandName, tenantId: tid }) => {
  const cachedLogo = findCachedItem('dashboard_logo_', tid);
  const logoDataUri = cachedLogo ? `data:image/svg+xml,${encodeURIComponent(cachedLogo)}` : null;

  useEffect(() => {
    // Inject cached theme CSS as inline <style>
    const cachedCss = findCachedItem('dashboard_theme_css_', tid);
    let styleEl;
    if (cachedCss) {
      styleEl = document.createElement('style');
      styleEl.setAttribute('data-tenant-theme-fallback', 'true');
      styleEl.textContent = cachedCss;
      document.head.appendChild(styleEl);
    }

    // Set cached favicon
    const cachedFavicon = findCachedItem('dashboard_favicon_', tid);
    if (cachedFavicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = cachedFavicon;
    }

    // Poll backend every 15s — reload automatically when it's back
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${backendUrl}/api/v1/tenant/visual-config`);
        if (res.ok) window.location.reload();
      } catch { /* still down */ }
    }, 15000);
    return () => {
      clearInterval(interval);
      if (styleEl) styleEl.remove();
    };
  }, [tid]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: '100vh',
      fontFamily: 'sans-serif', backgroundColor: 'var(--login-background-color, var(--tenant-background-color-primary, #fff))',
      color: 'var(--login-font-color, var(--tenant-black, #333))',
    }}>
      {/* Logo area — same position as login screen */}
      <div style={{ textAlign: 'center', paddingTop: '120px', marginBottom: '34px' }}>
        {logoDataUri ? (
          <img src={logoDataUri} alt={brandName || ''} style={{ height: '80px', width: 'auto' }} />
        ) : brandName ? (
          <p style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--tenant-primary-color, #555)' }}>{brandName}</p>
        ) : null}
      </div>
      {/* Message area — aligned with where the form would be */}
      <div style={{
        maxWidth: '480px', margin: '0 auto', padding: '16px',
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
          Servicio temporalmente no disponible
        </h1>
        <p style={{ lineHeight: 1.5, color: '#666' }}>
          Estamos trabajando para restablecer el servicio.
          La página se actualizará automáticamente cuando esté disponible.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '1.5rem', padding: '0.75rem 2rem', fontSize: '1rem',
            border: 'none', borderRadius: 'var(--tenant-buttom-radius, 4px)',
            cursor: 'pointer', color: '#fff',
            backgroundColor: 'var(--tenant-primary-color, #555)',
          }}
        >
          Reintentar
        </button>
      </div>
    </div>
  );
};

const ConfigProvider = ({ children }) => {
  const token = useSelector((state) => state.api?.token);
  const { visualConfig, loading, backendReachable } = useVisualConfig();
  const { bootstrap } = useBootstrap(token);

  // Apply bootstrap config to getConfig() system + tenant env config
  useEffect(() => {
    if (bootstrap?.config) {
      applyBootstrapConfig(bootstrap.config);

      // Re-register numeral locale from tenant config (currency symbol, separators)
      if (bootstrap.config.numeralLocale) {
        const localeDef = getNumeralLocale(bootstrap.config.numeralLocale);
        numeral.locales.es = localeDef;
      }
    }
    // Apply tenant-specific env config (URLs, GTM, etc.) from bootstrap
    if (bootstrap?.envConfig) {
      applyRuntimeEnv(bootstrap.envConfig);
    }
  }, [bootstrap]);

  // Load tenant-specific locale overrides post-login
  useEffect(() => {
    if (!token) return;
    const lang = i18n.getLanguage() || 'es';
    fetch(`${backendUrl}/api/v1/tenant/locales/${lang}`, {
      headers: {
        'X-Authentication-Token': token,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((overrides) => {
        if (overrides) {
          i18n.addResources(lang, 'translations', overrides);
        }
      })
      .catch((err) => console.warn('Tenant locale load failed', err));
  }, [token]);

  // Keep theme CSS in sync when visualConfig updates (e.g. configVersion change).
  // On first load, resolveTenant() already injected the CSS as inline <style>.
  useEffect(() => {
    if (!visualConfig?.themeUrl) return;
    // If resolveTenant already injected a <style>, update it; otherwise use a <link>
    const inlineStyle = document.querySelector("style[data-tenant-theme]");
    if (inlineStyle) {
      fetch(visualConfig.themeUrl)
        .then((r) => r.ok ? r.text() : null)
        .then((css) => { if (css) inlineStyle.textContent = css; })
        .catch(() => {});
    } else {
      let link = document.querySelector("link[data-tenant-theme]");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.setAttribute('data-tenant-theme', 'true');
        document.head.appendChild(link);
      }
      link.href = visualConfig.themeUrl;
    }
  }, [visualConfig?.themeUrl]);

  // Update favicon dynamically
  useEffect(() => {
    if (visualConfig?.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = visualConfig.faviconUrl;
    }
  }, [visualConfig?.faviconUrl]);

  // Backend down + not logged in = show friendly error page
  if (!loading && backendReachable === false && !token) {
    return <ServiceUnavailable brandName={visualConfig?.brandName} tenantId={visualConfig?.tenantId} />;
  }

  return (
    <ConfigContextProvider visualConfig={visualConfig} bootstrap={bootstrap}>
      {children}
    </ConfigContextProvider>
  );
};

// Persist store once at module level (not inside the component where it runs on every render)
// eslint-disable-next-line no-unused-vars
const persistor = persistStore(store, null);

export const CoreApp = () => {
  return (
    <Provider store={store}>
      <ConfigProvider>
        <PermissionsContextProvider>
          <QueryClientProvider client={queryClient}>
            <MainRouter />
          </QueryClientProvider>
        </PermissionsContextProvider>
      </ConfigProvider>
    </Provider>
  )
}
